import React, { useEffect, useId, useRef } from 'react';
import { FiX } from 'react-icons/fi';
import { firstFocusable, trapTab } from '../../utils/focus-trap';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  titleIcon?: React.ReactNode;
  size?: ModalSize;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** The element to focus when the dialog opens; the first focusable one otherwise. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /** Accessible name when the dialog has no visible title. */
  ariaLabel?: string;
  /** `side` docks the dialog to the right edge at full height (full screen below `sm`). */
  placement?: 'center' | 'side';
}

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
};

// Written out whole so Tailwind finds them: a docked panel spans the screen below `sm`.
const SIDE_SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-2xl',
  lg: 'sm:max-w-4xl',
  xl: 'sm:max-w-6xl',
};

/**
 * A modal dialog: `role="dialog"` with `aria-modal`, named by its title,
 * focus moved inside on open, Tab kept inside, focus returned to the opener
 * on close, Escape closes.
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  titleIcon,
  size = 'md',
  children,
  footer,
  initialFocusRef,
  ariaLabel,
  placement = 'center',
}) => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  // The latest onClose and initial focus target, read through refs so the
  // effect below runs on open and close only: callers pass inline arrows, and
  // re-running on every render would pull focus away while someone types.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const initialFocus = useRef(initialFocusRef);
  initialFocus.current = initialFocusRef;

  useEffect(() => {
    if (!isOpen) return undefined;
    const opener = document.activeElement as HTMLElement | null;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
      else if (e.key === 'Tab' && panelRef.current) trapTab(e, panelRef.current);
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    const target = initialFocus.current?.current ?? (panelRef.current ? firstFocusable(panelRef.current) : null) ?? panelRef.current;
    target?.focus();
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      opener?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen) return null;
  const side = placement === 'side';

  return (
    <div
      className={`fixed inset-0 z-50 flex bg-black/50 ${side ? 'justify-end' : 'items-center justify-center p-4'}`}
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
        className={side
          ? `bg-surface shadow-xl w-full ${SIDE_SIZE_CLASSES[size]} h-full overflow-auto focus:outline-none`
          : `bg-surface rounded-lg shadow-xl w-full ${SIZE_CLASSES[size]} max-h-[90vh] overflow-auto mx-4 focus:outline-none`}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-surface rounded-t-lg">
            <div className="flex items-center gap-2">
              {titleIcon}
              <h3 id={titleId} className="text-lg font-semibold text-content-primary">{title}</h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className="text-content-tertiary hover:text-content-secondary"
            >
              <FiX className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        )}
        <div className={title ? '' : 'relative'}>
          {!title && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className="absolute top-4 right-4 text-content-tertiary hover:text-content-secondary z-10"
            >
              <FiX className="w-5 h-5" aria-hidden="true" />
            </button>
          )}
          {children}
        </div>
        {footer && (
          <div className="flex justify-end gap-3 px-6 py-4 border-t bg-surface-secondary rounded-b-lg sticky bottom-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
